import { SOCIAL_LINKS } from "@/lib/site-config";
import { track } from "@/lib/analytics/mixpanel";

// Shared between the always-on custom launcher (components/ui/ChatwootLauncher)
// and the homepage teaser bubble (components/ui/ChatwootTeaser) — both load the
// SDK lazily (hover/click), rather than on every pageview, to stay well under
// Chatwoot's rate limit on new widget sessions per IP (5/hour). Config is
// resolved at RUNTIME from window.__ENV__ (written by env.sh into
// public/env-config.js at container startup), falling back to build-time
// process.env for local dev.
export type ChatwootConfig = { baseUrl: string; token: string };

const OPEN_POLL_INTERVAL_MS = 100;
const OPEN_POLL_TIMEOUT_MS = 10_000;

export function readChatwootConfig(): ChatwootConfig | null {
  const runtime = typeof window !== "undefined" ? window.__ENV__ : undefined;
  const baseUrl = runtime?.NEXT_PUBLIC_CHATWOOT_BASE_URL || process.env.NEXT_PUBLIC_CHATWOOT_BASE_URL;
  const token = runtime?.NEXT_PUBLIC_CHATWOOT_TOKEN || process.env.NEXT_PUBLIC_CHATWOOT_TOKEN;

  return baseUrl && token ? { baseUrl, token } : null;
}

export function loadChatwootSdk({ baseUrl, token }: ChatwootConfig) {
  if (document.querySelector<HTMLScriptElement>("script[data-chatwoot-sdk]")) return;

  // Must be set before sdk.js executes (the script reads it on bootstrap, not
  // just on `.run()`) — we hide Chatwoot's own launcher bubble entirely and
  // drive everything through our own animated ChatwootLauncher instead, since
  // the stock bubble alone was getting near-zero engagement.
  window.chatwootSettings = { ...window.chatwootSettings, hideMessageBubble: true };

  const script = document.createElement("script");
  script.src = `${baseUrl}/packs/js/sdk.js`;
  script.async = true;
  script.dataset.chatwootSdk = "";
  script.onload = () => {
    window.chatwootSDK?.run({ websiteToken: token, baseUrl });
  };
  document.body.appendChild(script);
}

/**
 * Ensures the SDK is loaded, then opens the widget as soon as it's ready.
 * `onUnavailable` fires if it never becomes ready in time — e.g. Chatwoot's
 * own rate limiter 429s the session bootstrap request — so callers can hand
 * the visitor an alternative contact path instead of a dead click.
 */
export function openChatwoot(config: ChatwootConfig, onUnavailable?: () => void) {
  if (window.$chatwoot) {
    window.$chatwoot.toggle("open");
    return;
  }

  loadChatwootSdk(config);

  let waited = 0;
  const handle = window.setInterval(() => {
    if (window.$chatwoot) {
      window.clearInterval(handle);
      window.$chatwoot.toggle("open");
      return;
    }
    waited += OPEN_POLL_INTERVAL_MS;
    if (waited >= OPEN_POLL_TIMEOUT_MS) {
      window.clearInterval(handle);
      onUnavailable?.();
    }
  }, OPEN_POLL_INTERVAL_MS);
}

/**
 * openChatwoot(), falling back to WhatsApp if the widget doesn't come up in
 * time — the visitor always has somewhere to go, even mid-rate-limit.
 */
export function openChatwootOrFallback(config: ChatwootConfig) {
  openChatwoot(config, () => {
    track("Chat Fallback To WhatsApp");
    window.open(SOCIAL_LINKS.whatsapp, "_blank", "noopener,noreferrer");
  });
}

// Cross-component signal so the always-visible ChatwootLauncher (rendered in
// the root layout) can react while the homepage-only ChatwootTeaser is
// showing a message — same window-event-bus pattern as CONSENT_CHANGE_EVENT
// in lib/analytics/mixpanel.ts, used because the two components don't share
// a subtree and a context provider would be overkill for one boolean.
export const CHAT_TEASER_ACTIVE_EVENT = "chat-teaser:active-change";

export function emitChatTeaserActive(active: boolean) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<boolean>(CHAT_TEASER_ACTIVE_EVENT, { detail: active }));
}

declare global {
  interface Window {
    __ENV__?: Record<string, string | undefined>;
    chatwootSettings?: { hideMessageBubble?: boolean; position?: "left" | "right"; locale?: string };
    chatwootSDK?: { run: (opts: { websiteToken: string; baseUrl: string }) => void };
    $chatwoot?: { toggle: (state?: "open" | "close") => void };
  }
}
